import {
  BillableIcon,
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Currency,
  ModalCard,
  SearchInput,
  Table,
  Tooltip,
} from '~/components';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '~/styles';
import { Card } from '~/components/ModalCard';
import { useFeatures } from '~/hooks';
import Fuse from 'fuse.js';

const StyledTable = styled(Table)`
  font-size: 0.875rem;
  margin-top: 1rem;

  opacity: ${({ fade }) => (fade ? 0.1 : 1)};

  height: 25rem;
  overflow-y: auto;

  ${Table.Cell} {
    align-items: flex-start;
    height: 100%;
    min-height: unset;
    padding: 1rem;
  }
`;

const StyledCheckbox = styled.div`
  > label > div {
    background: ${colors.white};
    width: 1.125rem;
    height: 1.125rem;
    font-size: 0.5rem;
    margin: 0;
  }
`;

const StyledModal = styled(ModalCard)`
  ${Card} {
    width: 64rem;
  }
`;

const StyledHeader = styled(Table.Header)`
  ${({ sticky }) =>
    sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
    `}
`;

function WorkspaceRolesModal({ workspaceRoles, onChange, onClose, action, roles }) {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const features = useFeatures();

  const [searchQuery, setSearchQuery] = useState('');

  const options = useMemo(() => {
    if (!searchQuery) return workspaceRoles;

    const fuse = new Fuse(workspaceRoles, { keys: ['name'], threshold: 0.4 });
    const results = fuse.search(searchQuery);

    return results.map((result) => result.item);
  }, [workspaceRoles, searchQuery]);

  const handleBulkSelectionChange = () => {
    setSelectedRoles(
      selected.some
        ? selectedRoles.filter(({ id }) => !selectedRoles.some((role) => id === role.id))
        : [
            ...selectedRoles,
            ...workspaceRoles.filter(
              (wr) =>
                !roles.some(
                  (cr) =>
                    (action === 'link' && cr.workspaceRoleId === wr.id) ||
                    (wr.name === cr.name && wr.practice?.id === cr.practice?.id && wr.location?.id === cr.location?.id),
                ),
            ),
          ],
    );
  };

  const handleAdd = () => {
    onChange(selectedRoles);
    onClose();
  };

  const selected = useMemo(() => {
    if (!selectedRoles) return {};

    const rolesById = _.groupBy(workspaceRoles, 'id');

    return {
      roles: selectedRoles.filter(({ id }) => !!rolesById[id]),

      get some() {
        return this.roles.length > 0;
      },
      get all() {
        return this.roles.length === workspaceRoles.length;
      },
    };
  }, [selectedRoles, workspaceRoles]);

  const handleSearchChange = ({ target: { value } }) => {
    setSearchQuery(value);
  };

  return (
    <StyledModal title={`${{ add: 'Add', link: 'Link' }[action]} Workspace Roles`} onClose={onClose}>
      <ModalCard.Body>
        <SearchInput
          value={searchQuery}
          placeholder="All"
          materialPlaceholder="Search"
          materialAlwaysVisible
          onChange={handleSearchChange}
        />
        <StyledTable>
          <StyledHeader sticky>
            <Table.Column align="center" width="3rem">
              <StyledCheckbox>
                <Checkbox
                  checked={selected.some || false}
                  partial={!selected.all || false}
                  onChange={handleBulkSelectionChange}
                />
              </StyledCheckbox>
            </Table.Column>
            <Table.Column width="17rem">Role Name</Table.Column>
            <Table.Column isVisible={features.practices} width="17rem">
              Practice
            </Table.Column>
            <Table.Column width="14rem">Location</Table.Column>
            <Table.Column align="right">Rate</Table.Column>
          </StyledHeader>
          <Table.Body>
            {options.map((role) => {
              const checked = !!selectedRoles.find((r) => r.id === role.id);

              let tooltip = 'This role already exists for this client.';

              const duplicated = roles.some((cr) => {
                if (action === 'link' && cr.workspaceRoleId === role.id) {
                  tooltip = 'This Role is already linked with the client.';
                  return true;
                }
                if (
                  cr.name === role.name &&
                  cr.practice?.id === role.practice?.id &&
                  cr.location?.id === role.location?.id
                ) {
                  if (cr.workspaceRoleId && action === 'link') tooltip = 'This Role is already linked with the client.';
                  return true;
                }
                return false;
              });

              const handleSelectionChange = () => {
                setSelectedRoles(checked ? selectedRoles.filter(({ id }) => id !== role.id) : [...selectedRoles, role]);
              };

              return (
                <Table.Row key={role.id}>
                  <Table.Cell>
                    <Tooltip message={duplicated ? tooltip : undefined}>
                      <StyledCheckbox>
                        <Checkbox
                          tooltip="please"
                          disabled={duplicated}
                          checked={checked}
                          onChange={handleSelectionChange}
                        />
                      </StyledCheckbox>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>{role.name}</Table.Cell>
                  <Table.Cell>{role.practice?.name}</Table.Cell>
                  <Table.Cell>{role.location?.name}</Table.Cell>
                  <Table.Cell>
                    <Currency
                      value={role.isBillable ? role.rate : null}
                      maximumFractionDigits={7}
                      currency={role.currency}
                    />
                    <BillableIcon style={{ marginLeft: '0.4rem' }} value={role.isBillable} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </StyledTable>
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button disabled={!selected.some} onClick={handleAdd}>
            {{ add: 'Add', link: 'Link' }[action]}
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </StyledModal>
  );
}

export default WorkspaceRolesModal;
